// microblog-lms/components/organisms/LectureEditForm/index.tsx
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { FileText, Layout, Code as CodeIcon, HelpCircle, Image as ImageIcon, Table2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import debounce from 'lodash.debounce';
import { usePrefixedNavigate as useNavigate, getRoutePath } from '@edusense/navigation-utils';
import MarkdownEditorModeComponent from './MarkdownEditorMode';
import BlockEditorModeComponent from './BlockEditorMode';
import * as S from './LectureEditForm.styles';
import { useUploadLectureResource } from '@edusense/course-client';
import { COURSE_EDIT_TEST_IDS } from '@edusense/shared-constants';
// XState 관련 import
import { useMachine, useSelector } from '@xstate/react';
// lectureFormMachine.ts에서 필요한 타입들을 명시적으로 type 한정자와 함께 import
import { lectureFormMachine } from './lectureFormMachine';
import { useCurriculumStore } from '@edusense/entities-course';
// 유틸리티 함수
const MIN_EDITOR_HEIGHT = 200;
const MAX_EDITOR_HEIGHT = 600;
const isEditorStillValid = (editor)=>{
    if (!editor) return false;
    try {
        return !!editor.getModel();
    } catch (e) {
        return false;
    }
};
// !!! 중요: 안정적인 ID 생성 로직으로 반드시 교체해야 합니다 !!!
const parseMarkdownToBlocksStable = (markdown, stableKey)=>{
    if (!markdown || typeof markdown !== 'string' || markdown.trim() === '') {
        return [
            {
                id: `empty-${stableKey}-${uuidv4()}`,
                type: 'markdown',
                content: '',
                isOpen: true,
                editorHeight: MIN_EDITOR_HEIGHT
            }
        ];
    }
    const blocks = markdown.split('\n---\n').map((content, index)=>({
            id: `parsed-${stableKey}-${index}-${uuidv4()}`,
            type: 'markdown',
            content: content.trim(),
            isOpen: true,
            editorHeight: MIN_EDITOR_HEIGHT
        }));
    if (blocks.length === 0 && markdown.trim() !== '') {
        return [
            {
                id: `fallback-${stableKey}-${uuidv4()}`,
                type: 'markdown',
                content: markdown,
                isOpen: true,
                editorHeight: MIN_EDITOR_HEIGHT
            }
        ];
    }
    return blocks;
};
const sanitizeLectureTitle = (value)=>{
    const trimmed = (value ?? '').trim();
    return trimmed.length > 0 ? trimmed : '제목 없음';
};
const LectureEditForm = ({ selectedLesson, courseId, onDirtyStateChange })=>{
    console.log('🔍 [LectureEditForm] Component rendering:', {
        selectedLesson,
        courseId,
        hasOnDirtyStateChange: !!onDirtyStateChange
    });
    const navigate = useNavigate();
    const uploadLectureResourceMutation = useUploadLectureResource();
    const lessonFromStore = useCurriculumStore((state)=>selectedLesson ? state.sections.find((s)=>s.id === selectedLesson.sectionId)?.lessons.find((l)=>l.id === selectedLesson.lessonId) : null);
    const zustandUpdateLesson = useCurriculumStore((state)=>state.updateLesson);
    const zustandUpdateSelectedLessonMarkdown = useCurriculumStore((state)=>state.updateSelectedLessonMarkdown);
    const storeSelectedLesson = useCurriculumStore((state)=>state.selectedLesson);
    const { control, handleSubmit, formState: { isDirty: isRHFDirty, errors: rhfErrors }, reset, getValues, watch } = useForm({
        defaultValues: {
            title: '제목 없음',
            markdown: '',
            blocks: []
        },
        mode: 'onChange'
    });
    const [state, send, service] = useMachine(lectureFormMachine, {
        input: {
            courseId: courseId,
            initialSelectedLesson: selectedLesson,
            initialLessonData: lessonFromStore ? {
                id: lessonFromStore.id,
                title: lessonFromStore.title,
                // 스토어에 markdownContent 필드가 있다면 사용, 없다면 markdown 필드 사용 (또는 실제 필드명으로)
                markdown: lessonFromStore.markdownContent || lessonFromStore.markdown
            } : null,
            stableKeyForBlockIds: selectedLesson?.lessonId || uuidv4(),
            // doUpdateLessonInStore는 Promise<MachineLesson>을 반환해야 함
            doUpdateLessonInStore: async (sectionId, lessonId, lessonData)=>{
                // Zustand 스토어의 Lesson 타입과 머신의 Lesson 타입 간 필드명 불일치 처리
                const storeUpdateData = {
                    id: lessonId,
                    title: lessonData.title,
                    markdown: lessonData.markdown
                };
                // 스토어에 없는 필드는 제거하거나, 스토어 타입에 맞게 변환
                // delete (storeUpdateData as any).markdown; 
                await zustandUpdateLesson(sectionId, lessonId, storeUpdateData);
                // 머신이 기대하는 타입으로 반환
                return {
                    id: lessonId,
                    title: lessonData.title || '',
                    markdown: lessonData.markdown
                };
            },
            doParseMarkdown: parseMarkdownToBlocksStable
        }
    });
    const xFormData = useSelector(service, (snapshot)=>snapshot.context.formData);
    const xEditorMode = useSelector(service, (snapshot)=>snapshot.context.editorMode);
    const xIsDirty = useSelector(service, (snapshot)=>snapshot.context.isDirty);
    const xErrorMessage = useSelector(service, (snapshot)=>snapshot.context.errorMessage);
    const editorRefs = useRef({});
    const monacoRef = useRef(null);
    const dndListenersRef = useRef({});
    const watchedMarkdown = watch('markdown');
    const debouncedSaveMarkdown = useCallback(debounce((newMarkdown)=>{
        if (storeSelectedLesson && selectedLesson && storeSelectedLesson.lessonId === selectedLesson.lessonId && storeSelectedLesson.sectionId === selectedLesson.sectionId) {
            const currentLessonInStore = useCurriculumStore.getState().sections.find((s)=>s.id === storeSelectedLesson.sectionId)?.lessons.find((l)=>l.id === storeSelectedLesson.lessonId);
            if (currentLessonInStore && currentLessonInStore.markdown !== newMarkdown) {
                zustandUpdateSelectedLessonMarkdown(newMarkdown);
                if (onDirtyStateChange) {
                    onDirtyStateChange(true);
                }
            }
        }
    }, 1000), [
        selectedLesson,
        storeSelectedLesson,
        zustandUpdateSelectedLessonMarkdown,
        onDirtyStateChange
    ]);
    useEffect(()=>{
        if (watchedMarkdown !== undefined && state.matches('editing')) {
            if (isRHFDirty) {
                debouncedSaveMarkdown(watchedMarkdown);
            }
        }
    }, [
        watchedMarkdown,
        debouncedSaveMarkdown,
        state,
        isRHFDirty
    ]);
    useEffect(()=>{
        if (onDirtyStateChange) {
            onDirtyStateChange(isRHFDirty);
        }
        send({
            type: 'SET_DIRTY_STATUS',
            isDirty: isRHFDirty
        });
    }, [
        isRHFDirty,
        onDirtyStateChange,
        send
    ]);
    const [dropdownPosition, setDropdownPosition] = React.useState(null);
    const onCloseAddMenu = useCallback(()=>setDropdownPosition(null), []);
    const [activeDropZone, setActiveDropZone] = React.useState(null);
    const getDefaultContentForBlockType = useCallback((type)=>{
        switch(type){
            case 'markdown':
                return '# 제목\n\n일반 텍스트';
            case 'table':
                return '| 제목 1 | 제목 2 |\n| --- | --- |\n| 내용 1 | 내용 2 |';
            case 'terminal':
                return '```bash\n# 여기에 명령어를 입력하세요.\n```';
            case 'quiz':
                return ':::quiz\ntype: multiple-choice\nquestion: 다음 중 올바른 것은?\noptions:\n  - 답 1\n  - 답 2\n  - 답 3\nanswerIndex: 0\nexplanation: 답 1이 정답입니다.\n:::';
            case 'html':
                return '<div class="custom-html-block">\n  <p>HTML 내용을 여기에 작성하세요.</p>\n</div>';
            case 'image':
                return '![이미지 설명](이미지 URL)';
            default:
                return '';
        }
    }, []);
    const addBlockAt = useCallback((type, index, content, metadata)=>{
        send({
            type: 'ADD_BLOCK',
            blockType: type,
            index: index,
            content: content,
            metadata: metadata,
            getDefaultContent: getDefaultContentForBlockType
        });
        if (dropdownPosition) onCloseAddMenu();
    }, [
        send,
        getDefaultContentForBlockType,
        dropdownPosition,
        onCloseAddMenu
    ]);
    const onBlockDragOver = useCallback((e, index)=>{
        e.preventDefault();
        setActiveDropZone(index);
    }, [
        setActiveDropZone
    ]);
    const onDragLeave = useCallback(()=>setActiveDropZone(null), [
        setActiveDropZone
    ]);
    const onBlockDrop = useCallback((e, index)=>{
        e.preventDefault();
        const blockTypeTransfer = e.dataTransfer.getData('blockType');
        if (blockTypeTransfer && Object.values([
            'markdown',
            'table',
            'terminal',
            'quiz',
            'html',
            'image'
        ]).includes(blockTypeTransfer)) {
            addBlockAt(blockTypeTransfer, index);
        }
        setActiveDropZone(null);
    }, [
        addBlockAt,
        setActiveDropZone
    ]);
    const onOpenAddMenu = useCallback((index, event)=>{
        const currentTarget = event.currentTarget;
        const rect = currentTarget.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const menuHeightEstimate = 280;
        const position = windowHeight - rect.bottom < menuHeightEstimate && rect.top > menuHeightEstimate ? 'top' : 'bottom';
        const y = position === 'top' ? rect.top - menuHeightEstimate + window.scrollY : rect.bottom + window.scrollY;
        setDropdownPosition({
            index,
            x: rect.left + window.scrollX,
            y,
            position
        });
    }, [
        setDropdownPosition
    ]);
    const uploadResourceFile = useCallback(async (file)=>{
        const currentCourseId = state.context.courseId;
        const currentLessonIdInMachine = state.context.selectedLessonInput?.lessonId;
        if (!currentCourseId || currentCourseId === 'new') {
            toast.error('파일을 업로드하려면 먼저 강의 초안을 저장해야 합니다.');
            return null;
        }
        try {
            // lectureId가 필요하므로 확인
            if (!currentLessonIdInMachine) {
                toast.error('파일을 업로드하려면 먼저 강의를 선택해야 합니다.');
                return null;
            }
            // course-server의 lecture resource upload API 사용
            const response = await uploadLectureResourceMutation.mutateAsync({
                courseId: currentCourseId,
                lectureId: currentLessonIdInMachine,
                data: {
                    file,
                    title: file.name
                }
            });
            // 응답은 { url: string, cdnUrl?: string, mediaId: string }
            // CDN URL을 우선 사용, 없으면 원본 URL 사용
            const uploadedUrl = response?.cdnUrl || response?.url;
            const uploadedName = file.name;
            const uploadedId = response?.mediaId || response?.id;
            if (!uploadedUrl) {
                throw new Error('Upload response did not contain a CDN URL.');
            }
            toast.success(`${uploadedName} 업로드 완료!`);
            return {
                url: uploadedUrl,
                name: uploadedName,
                id: uploadedId
            };
        } catch (error) {
            console.error('Failed to upload file', error);
            const message = error?.message ?? (error?.response?.data?.message || '알 수 없는 오류');
            toast.error(`${file.name} 업로드 실패: ${message}`);
            return null;
        }
    }, [
        uploadLectureResourceMutation,
        state.context.courseId,
        state.context.selectedLessonInput
    ]);
    const updateBlockContent = useCallback((blockId, value)=>{
        const currentBlocks = xFormData.blocks || [];
        const newContent = value || '';
        const newBlocks = currentBlocks.map((block)=>block.id === blockId ? {
                ...block,
                content: newContent
            } : block);
        send({
            type: 'UPDATE_BLOCKS',
            blocks: newBlocks
        });
    }, [
        send,
        xFormData.blocks
    ]);
    const handleFileDropInEditor = useCallback(async (event, editorId)=>{
        event.preventDefault();
        event.stopPropagation();
        if (!monacoRef.current) {
            toast.error('에디터가 준비되지 않았습니다.');
            return;
        }
        const editor = editorRefs.current[editorId];
        if (!isEditorStillValid(editor)) {
            toast.error('드롭 대상 에디터가 유효하지 않습니다.');
            return;
        }
        const dataTransfer = event.dataTransfer || event.dataTransfer;
        if (!dataTransfer || dataTransfer.files.length === 0) return;
        const file = dataTransfer.files[0];
        const clientX = event.clientX ?? event.clientX;
        const clientY = event.clientY ?? event.clientY;
        const target = editor.getTargetAtClientPoint(clientX, clientY);
        if (!target || !target.position) {
            toast.error('드롭 위치를 특정할 수 없습니다.');
            return;
        }
        const position = target.position;
        const tempTextIdSuffix = Date.now();
        const tempText = `[${file.name} 업로드 중...]`;
        const placeholderId = `upload-placeholder-${uuidv4()}`;
        const placeholderText = `<!-- ${placeholderId} -->${tempText}<!-- ${placeholderId} -->`;
        editor.executeEdits(`upload-insert-${tempTextIdSuffix}`, [
            {
                range: new monacoRef.current.Range(position.lineNumber, position.column, position.lineNumber, position.column),
                text: placeholderText,
                forceMoveMarkers: true
            }
        ]);
        const model = editor.getModel();
        if (!model) return;
        const placeholderStartLine = position.lineNumber;
        const placeholderStartColumn = position.column;
        const placeholderEndLine = position.lineNumber;
        const placeholderEndColumn = position.column + placeholderText.length;
        const tempRange = new monacoRef.current.Range(placeholderStartLine, placeholderStartColumn, placeholderEndLine, placeholderEndColumn);
        const toastId = `upload-toast-${tempTextIdSuffix}`;
        try {
            toast.loading(`${file.name} 업로드 중...`, {
                id: toastId
            });
            const uploaded = await uploadResourceFile(file);
            const currentEditor = editorRefs.current[editorId];
            const editorValid = isEditorStillValid(currentEditor);
            if (!editorValid) {
                toast.dismiss(toastId);
                if (uploaded) toast.success(`${uploaded.name} 업로드 완료! (에디터가 사라졌습니다)`);
                return;
            }
            if (uploaded && uploaded.url) {
                let contentToInsert = '';
                const fileName = uploaded.name;
                const isVideoFile = file.type.startsWith('video/') || /\.(mp4|mov|avi|wmv|flv|webm|mkv|m3u8)$/i.test(fileName);
                const isHls = uploaded.url.includes('/hls/') || uploaded.url.endsWith('.m3u8');
                if (isHls || isVideoFile) {
                    contentToInsert = `:::video\n{src="${uploaded.url}" title="${fileName}"}\n:::`;
                } else if (file.type.startsWith('image/')) {
                    contentToInsert = `![${fileName}](${uploaded.url})`;
                } else {
                    contentToInsert = `[${fileName}](${uploaded.url})`;
                }
                const currentEditorValue = currentEditor.getValue();
                const placeholderRegex = new RegExp(`<!-- ${placeholderId} -->.*?<!-- ${placeholderId} -->`, 'gs');
                const currentModel = currentEditor.getModel();
                if (!currentModel) {
                    toast.error("에디터 모델을 찾을 수 없어 플레이스홀더를 교체할 수 없습니다.", {
                        id: toastId
                    });
                    return;
                }
                let match = placeholderRegex.exec(currentEditorValue);
                if (match) {
                    const startOffset = match.index;
                    const endOffset = startOffset + match[0].length;
                    const startPos = currentModel.getPositionAt(startOffset);
                    const endPos = currentModel.getPositionAt(endOffset);
                    const rangeToReplace = new monacoRef.current.Range(startPos.lineNumber, startPos.column, endPos.lineNumber, endPos.column);
                    currentEditor.executeEdits(`upload-replace-${tempTextIdSuffix}`, [
                        {
                            range: rangeToReplace,
                            text: contentToInsert,
                            forceMoveMarkers: true
                        }
                    ]);
                    toast.success(`${fileName} 처리 완료!`, {
                        id: toastId
                    });
                } else {
                    currentEditor.executeEdits(`upload-replace-fallback-${tempTextIdSuffix}`, [
                        {
                            range: tempRange,
                            text: contentToInsert,
                            forceMoveMarkers: true
                        }
                    ]);
                    toast.success(`${fileName} 처리 완료! (Fallback)`, {
                        id: toastId
                    });
                }
                // 자료 관리에 업로드된 파일 추가
                const resourceAddedEvent = new CustomEvent('resourceAdded', {
                    detail: {
                        id: uploaded.id || Math.random().toString(36).substr(2, 9),
                        name: fileName,
                        type: 'file',
                        url: uploaded.url,
                        mimeType: file.type,
                        size: file.size,
                        uploadedAt: new Date().toISOString()
                    }
                });
                window.dispatchEvent(resourceAddedEvent);
                if (editorId.startsWith('block-')) {
                    updateBlockContent(editorId, currentEditor.getValue());
                } else {
                    send({
                        type: 'UPDATE_MARKDOWN',
                        markdown: currentEditor.getValue() || ''
                    });
                }
            } else {
                const currentEditorValue = currentEditor.getValue();
                const placeholderRegex = new RegExp(`<!-- ${placeholderId} -->.*?<!-- ${placeholderId} -->`, 'gs');
                const currentModel = currentEditor.getModel();
                if (!currentModel) {
                    toast.error("에디터 모델을 찾을 수 없어 플레이스홀더를 정리할 수 없습니다.", {
                        id: toastId
                    });
                    return;
                }
                let match = placeholderRegex.exec(currentEditorValue);
                if (match) {
                    const startOffset = match.index;
                    const endOffset = startOffset + match[0].length;
                    const startPos = currentModel.getPositionAt(startOffset);
                    const endPos = currentModel.getPositionAt(endOffset);
                    const rangeToReplace = new monacoRef.current.Range(startPos.lineNumber, startPos.column, endPos.lineNumber, endPos.column);
                    currentEditor.executeEdits(`upload-clear-${tempTextIdSuffix}`, [
                        {
                            range: rangeToReplace,
                            text: '',
                            forceMoveMarkers: true
                        }
                    ]);
                }
                toast.error(`${file.name} 업로드 실패.`, {
                    id: toastId
                });
            }
        } catch (error) {
            const editorOnCatch = editorRefs.current[editorId];
            if (isEditorStillValid(editorOnCatch)) {
                const currentEditorValue = editorOnCatch.getValue();
                const placeholderRegex = new RegExp(`<!-- ${placeholderId} -->.*?<!-- ${placeholderId} -->`, 'gs');
                const currentModel = editorOnCatch.getModel();
                if (currentModel) {
                    let match = placeholderRegex.exec(currentEditorValue);
                    if (match) {
                        const startOffset = match.index;
                        const endOffset = startOffset + match[0].length;
                        const startPos = currentModel.getPositionAt(startOffset);
                        const endPos = currentModel.getPositionAt(endOffset);
                        const rangeToReplace = new monacoRef.current.Range(startPos.lineNumber, startPos.column, endPos.lineNumber, endPos.column);
                        editorOnCatch.executeEdits(`upload-error-clear-${tempTextIdSuffix}`, [
                            {
                                range: rangeToReplace,
                                text: '',
                                forceMoveMarkers: true
                            }
                        ]);
                    }
                }
            }
            toast.error(`${file.name} 업로드 중 에러 발생.`, {
                id: toastId
            });
            console.error("Error during file drop and upload process:", error);
        }
    }, [
        uploadResourceFile,
        send,
        updateBlockContent,
        monacoRef,
        editorRefs
    ]);
    const onRHFSubmit = (rhfData)=>{
        send({
            type: 'SAVE_LESSON'
        });
    };
    // handleSubmit으로 감싸진 실제 제출 함수를 생성합니다.
    const handleActualSubmit = handleSubmit(onRHFSubmit);
    const handleToggleEditorMode = ()=>send({
            type: 'TOGGLE_EDITOR_MODE'
        });
    const handleDeleteLecture = useCallback(()=>{
        // TODO: Implement lecture deletion logic
        toast.error('강의 삭제 기능은 아직 구현되지 않았습니다.');
    }, []);
    const removeBlock = useCallback((id)=>{
        send({
            type: 'UPDATE_BLOCKS',
            blocks: (xFormData.blocks || []).filter((block)=>block.id !== id)
        });
    }, [
        send,
        xFormData.blocks
    ]);
    const handleDragEnd = useCallback((result)=>{
        if (!result.destination) return;
        const currentBlocks = xFormData.blocks || [];
        const newBlocks = Array.from(currentBlocks);
        const [removed] = newBlocks.splice(result.source.index, 1);
        newBlocks.splice(result.destination.index, 0, removed);
        send({
            type: 'UPDATE_BLOCKS',
            blocks: newBlocks
        });
    }, [
        send,
        xFormData.blocks
    ]);
    const toggleBlockOpen = useCallback((id)=>{
        send({
            type: 'UPDATE_BLOCKS',
            blocks: (xFormData.blocks || []).map((block)=>block.id === id ? {
                    ...block,
                    isOpen: !block.isOpen
                } : block)
        });
    }, [
        send,
        xFormData.blocks
    ]);
    const navigateToEditorGuide = useCallback((target)=>{
        const rawPath = `/learn/editor-guides/${target}`;
        const resolved = getRoutePath(rawPath);
        const normalizedPath = resolved.startsWith('/') ? resolved : `/${resolved}`;
        try {
            navigate(normalizedPath);
        } catch (error) {
            console.warn('navigateToEditorGuide fallback navigation', {
                target,
                error
            });
            if (typeof window !== 'undefined') {
                window.location.href = normalizedPath;
            }
        }
    }, [
        navigate
    ]);
    const calculateAndSetEditorHeight = useCallback((blockId, editor)=>{
        if (!isEditorStillValid(editor)) return;
        try {
            const contentHeight = editor.getContentHeight();
            const newHeight = Math.max(MIN_EDITOR_HEIGHT, Math.min(contentHeight + 20, MAX_EDITOR_HEIGHT));
            const currentBlocks = xFormData.blocks || [];
            const blockToUpdate = currentBlocks.find((b)=>b.id === blockId);
            if (blockToUpdate && blockToUpdate.editorHeight !== newHeight) {
                send({
                    type: 'UPDATE_BLOCKS',
                    blocks: currentBlocks.map((b)=>b.id === blockId ? {
                            ...b,
                            editorHeight: newHeight
                        } : b)
                });
            }
        } catch (e) {
            console.warn("Error calculating editor height:", e);
        }
    }, [
        xFormData.blocks,
        send
    ]);
    const handleEditorDidMount = useCallback((editor, monacoInstance, blockOrEditorId)=>{
        monacoRef.current = monacoInstance;
        editorRefs.current[blockOrEditorId] = editor;
        const editorDomNode = editor.getDomNode();
        if (editorDomNode) {
            const handleDragOverInternal = (event)=>{
                event.preventDefault();
                event.stopPropagation();
                if (event.dataTransfer) event.dataTransfer.dropEffect = event.dataTransfer.types.includes('Files') ? 'copy' : 'none';
            };
            const boundHandleDrop = (event)=>handleFileDropInEditor(event, blockOrEditorId);
            editorDomNode.addEventListener('dragover', handleDragOverInternal);
            editorDomNode.addEventListener('drop', boundHandleDrop);
            dndListenersRef.current[blockOrEditorId] = {
                dragover: handleDragOverInternal,
                drop: boundHandleDrop
            };
        }
        if (blockOrEditorId.startsWith('block-')) {
            if (isEditorStillValid(editor) && typeof editor.onDidChangeModelContent === 'function') {
                editor.onDidChangeModelContent(()=>{
                    if (service.getSnapshot().matches('editing')) {
                        calculateAndSetEditorHeight(blockOrEditorId, editor);
                    }
                });
            }
            calculateAndSetEditorHeight(blockOrEditorId, editor);
        }
    }, [
        handleFileDropInEditor,
        calculateAndSetEditorHeight,
        service
    ]);
    useEffect(()=>{
        return ()=>{
            Object.entries(dndListenersRef.current).forEach(([id, listeners])=>{
                const editorNode = editorRefs.current[id]?.getDomNode();
                if (editorNode && listeners) {
                    editorNode.removeEventListener('dragover', listeners.dragover);
                    editorNode.removeEventListener('drop', listeners.drop);
                }
            });
            dndListenersRef.current = {};
        };
    }, []);
    const getBlockTypeIcon = useCallback((type)=>{
        switch(type){
            case 'markdown':
                return /*#__PURE__*/ _jsx(FileText, {
                    size: 16
                });
            case 'table':
                return /*#__PURE__*/ _jsx(Table2, {
                    size: 16
                });
            case 'terminal':
                return /*#__PURE__*/ _jsx(CodeIcon, {
                    size: 16
                });
            case 'quiz':
                return /*#__PURE__*/ _jsx(HelpCircle, {
                    size: 16
                });
            case 'html':
                return /*#__PURE__*/ _jsx(Layout, {
                    size: 16
                });
            case 'image':
                return /*#__PURE__*/ _jsx(ImageIcon, {
                    size: 16
                });
            default:
                return /*#__PURE__*/ _jsx(FileText, {
                    size: 16
                });
        }
    }, []);
    const getBlockTypeLabel = useCallback((type)=>{
        switch(type){
            case 'markdown':
                return '마크다운';
            case 'table':
                return '표';
            case 'terminal':
                return '터미널';
            case 'quiz':
                return '퀴즈';
            case 'html':
                return 'HTML';
            case 'image':
                return '이미지';
            default:
                return '알 수 없음';
        }
    }, []);
    const getBlockSummary = useCallback((block)=>{
        const content = block.content || '';
        if (block.type === 'markdown') {
            const titleMatch = content.match(/^#+\s+(.+)$/m);
            if (titleMatch) return titleMatch[1];
            const firstLine = content.split('\n')[0];
            return firstLine.substring(0, 50) + (firstLine.length > 50 ? '...' : '') || '빈 마크다운';
        }
        return getBlockTypeLabel(block.type); // 로컬 BlockType으로 캐스팅
    }, [
        getBlockTypeLabel
    ]);
    useEffect(()=>{
        const machineLessonFromStore = lessonFromStore ? {
            id: lessonFromStore.id,
            title: lessonFromStore.title,
            markdown: lessonFromStore.markdownContent || lessonFromStore.markdown
        } : undefined;
        send({
            type: 'INIT_OR_UPDATE_LESSON',
            lessonInput: selectedLesson,
            courseId: courseId,
            lessonDataFromStore: machineLessonFromStore
        });
    }, [
        selectedLesson,
        courseId,
        lessonFromStore,
        send
    ]);
    useEffect(()=>{
        if (state.value === 'idle' || !xFormData) return;
        const currentRHFValues = getValues();
        let changed = false;
        if (xFormData.title !== undefined && currentRHFValues.title !== xFormData.title) {
            changed = true;
        }
        if (xFormData.markdown !== undefined && currentRHFValues.markdown !== xFormData.markdown) {
            changed = true;
        }
        const xFormBlocksString = JSON.stringify(xFormData.blocks || []);
        const currentRHFBlocksString = JSON.stringify(currentRHFValues.blocks || []);
        if (xFormBlocksString !== currentRHFBlocksString) {
            changed = true;
        }
        if (changed && (state.matches('editing') || state.matches('initializing') || state.matches('parsingMarkdown'))) {
            reset(xFormData, {
                keepDirty: true
            });
        }
    }, [
        xFormData,
        state.value,
        reset,
        getValues,
        state.context.isDirty
    ]);
    console.log('🔍 [LectureEditForm] State check:', {
        stateValue: state.value,
        matches_idle: state.matches('idle'),
        matches_initializing: state.matches('initializing'),
        selectedLesson,
        contextSelectedLesson: state.context.selectedLessonInput
    });
    if (state.matches('idle') || state.matches('initializing') && !selectedLesson && !state.context.selectedLessonInput) {
        console.log('🔍 [LectureEditForm] Early return: No lesson selected');
        return /*#__PURE__*/ _jsx(S.FormContainer, {
            children: /*#__PURE__*/ _jsx("p", {
                children: "편집할 강의를 선택해주세요."
            })
        });
    }
    if (state.matches('initializing') && (selectedLesson || state.context.selectedLessonInput)) {
        console.log('🔍 [LectureEditForm] Early return: Loading lesson');
        return /*#__PURE__*/ _jsx(S.FormContainer, {
            children: /*#__PURE__*/ _jsx("p", {
                children: "강의 정보 로딩 중..."
            })
        });
    }
    const editorOptions = {
        minimap: {
            enabled: false
        },
        fontSize: 14,
        wordWrap: 'on',
        automaticLayout: true,
        scrollBeyondLastLine: false,
        lineNumbers: 'on',
        renderLineHighlight: 'none',
        tabSize: 2
    };
    return /*#__PURE__*/ _jsxs(S.FormContainer, {
        children: [
            /*#__PURE__*/ _jsx("h2", {
                children: "강의 콘텐츠 편집"
            }),
            /*#__PURE__*/ _jsxs(S.FormGroup, {
                children: [
                    /*#__PURE__*/ _jsx(S.Label, {
                        htmlFor: "lectureTitle",
                        children: "강의 제목"
                    }),
                    /*#__PURE__*/ _jsx(Controller, {
                        name: "title",
                        control: control,
                        rules: {
                            required: '강의 제목은 필수입니다.'
                        },
                        render: ({ field })=>/*#__PURE__*/ _jsx(S.Input, {
                                id: "lectureTitle",
                                "data-testid": COURSE_EDIT_TEST_IDS.CURRICULUM_LECTURE_TITLE_INPUT,
                                value: field.value ?? '',
                                placeholder: "제목 없음",
                                onChange: (event)=>{
                                    field.onChange(event.target.value);
                                    send({
                                        type: 'UPDATE_TITLE',
                                        title: event.target.value
                                    });
                                },
                                onBlur: (event)=>{
                                    const sanitized = sanitizeLectureTitle(event.target.value);
                                    if (sanitized !== event.target.value) {
                                        event.target.value = sanitized;
                                    }
                                    field.onChange(sanitized);
                                    send({
                                        type: 'UPDATE_TITLE',
                                        title: sanitized
                                    });
                                }
                            })
                    }),
                    rhfErrors.title && /*#__PURE__*/ _jsx(S.ErrorMessage, {
                        children: rhfErrors.title.message
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(S.SectionDivider, {}),
            /*#__PURE__*/ _jsxs(S.FormGroup, {
                children: [
                    /*#__PURE__*/ _jsx(S.SectionTitle, {
                        children: "강의 내용"
                    }),
                    /*#__PURE__*/ _jsxs(S.EditorModeToggle, {
                        children: [
                            /*#__PURE__*/ _jsxs(S.ToggleButton, {
                                $active: xEditorMode === 'markdown',
                                onClick: handleToggleEditorMode,
                                disabled: state.matches('parsingMarkdown'),
                                children: [
                                    /*#__PURE__*/ _jsx(FileText, {
                                        size: 16
                                    }),
                                    " 마크다운 에디터"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(S.ToggleButton, {
                                $active: xEditorMode === 'block',
                                onClick: handleToggleEditorMode,
                                disabled: state.matches('parsingMarkdown'),
                                children: [
                                    /*#__PURE__*/ _jsx(Layout, {
                                        size: 16
                                    }),
                                    " 블록 에디터"
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(S.EditorHelpRow, {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: "에디터 사용이 처음이라면?"
                            }),
                            /*#__PURE__*/ _jsxs(S.EditorHelpLink, {
                                type: "button",
                                onClick: ()=>navigateToEditorGuide('markdown'),
                                children: [
                                    /*#__PURE__*/ _jsx(FileText, {
                                        size: 14
                                    }),
                                    " 마크다운 가이드 보기"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(S.EditorHelpLink, {
                                type: "button",
                                onClick: ()=>navigateToEditorGuide('block'),
                                children: [
                                    /*#__PURE__*/ _jsx(Layout, {
                                        size: 14
                                    }),
                                    " 블록 가이드 보기"
                                ]
                            })
                        ]
                    }),
                    state.matches('parsingMarkdown') && /*#__PURE__*/ _jsx(S.FormContainer, {
                        children: /*#__PURE__*/ _jsx("p", {
                            children: "콘텐츠를 블록으로 변환 중입니다..."
                        })
                    }),
                    (state.matches('editing') || state.matches('saving')) && xFormData && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            xEditorMode === 'block' && /*#__PURE__*/ _jsx(BlockEditorModeComponent, {
                                blocks: xFormData.blocks || [],
                                activeDropZone: activeDropZone,
                                dropdownPosition: dropdownPosition,
                                editorOptions: editorOptions,
                                onDragEnd: handleDragEnd,
                                onBlockDragOver: onBlockDragOver,
                                onDragLeave: onDragLeave,
                                onBlockDrop: onBlockDrop,
                                onOpenAddMenu: onOpenAddMenu,
                                onCloseAddMenu: onCloseAddMenu,
                                onAddBlockAt: addBlockAt,
                                onBlockContentChange: updateBlockContent,
                                onToggleBlockOpen: toggleBlockOpen,
                                onRemoveBlock: removeBlock,
                                onBlockEditorMount: handleEditorDidMount,
                                getBlockTypeIcon: getBlockTypeIcon,
                                getBlockTypeLabel: getBlockTypeLabel,
                                getBlockSummary: getBlockSummary
                            }),
                            console.log('🔍 [LectureEditForm] xEditorMode state:', {
                                xEditorMode,
                                isMarkdown: xEditorMode === 'markdown'
                            }),
                            xEditorMode === 'markdown' && /*#__PURE__*/ _jsx(Controller, {
                                name: "markdown",
                                control: control,
                                render: ({ field })=>/*#__PURE__*/ _jsx(MarkdownEditorModeComponent, {
                                        markdownText: field.value || '',
                                        previewContent: field.value || '',
                                        editorOptions: editorOptions,
                                        onMarkdownChange: (value)=>{
                                            field.onChange(value);
                                            send({
                                                type: 'UPDATE_MARKDOWN',
                                                markdown: value || ''
                                            });
                                        },
                                        onEditorMount: (editor, monaco)=>handleEditorDidMount(editor, monaco, 'main-editor')
                                    })
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
export default LectureEditForm;
